/**
 * Karten (Phase 3): Eine Karte ist eine Notiz vom Typ `karte`; das Bild steht
 * Obsidian-kompatibel im Frontmatter (`image: "[[Dungeon.jpg]]"`). Raster, Pins,
 * Tokens und Nebel liegen in eigenen Tabellen. Koordinaten in Bildpixeln.
 */
import { z } from 'zod';

export const MAP_TYPE_KEY = 'karte';

/** Datei der Kampagne (Bildauswahl). */
export interface FileEntry {
  id: string;
  name: string;
  folder: string;
  mime: string;
  size: number;
  url: string;
}
export const MAP_IMAGE_KEY = 'image';
/** Frontmatter-Feld für das Token-Bild einer Kreatur. */
export const TOKEN_IMAGE_KEY = 'token';

export type GridType = 'none' | 'square' | 'hex';

export interface MapGrid {
  type: GridType;
  /** Kantenlänge eines Felds in Bildpixeln (Hex: Abstand gegenüberliegender Kanten). */
  size: number;
  offsetX: number;
  offsetY: number;
  /** Maßstab: Fuß bzw. Meter je Feld, nur Anzeige. */
  cellDistance: number;
  cellUnit: string;
  color: string;
  opacity: number;
}

export const DEFAULT_GRID: MapGrid = { type: 'none', size: 70, offsetX: 0, offsetY: 0, cellDistance: 1.5, cellUnit: 'm', color: '#000000', opacity: 0.35 };

/** Ein Schritt im Nebel: Bereich aufdecken oder (mit `hide`) wieder verdecken. */
export type FogShape = (
  | { type: 'rect'; x: number; y: number; w: number; h: number }
  | { type: 'poly'; points: number[] }
  /** Pinselstrich: Linie mit Breite. */
  | { type: 'line'; points: number[]; width: number }
) & { hide?: boolean };

export interface FogState {
  enabled: boolean;
  /** Schritte in Reihenfolge; anfangs ist alles verdeckt. */
  revealed: FogShape[];
}

export const DEFAULT_FOG: FogState = { enabled: false, revealed: [] };

export type PinVisibility = 'dm' | 'all';

export interface MapPinDTO {
  id: string;
  x: number;
  y: number;
  /** Eigene Beschriftung; leer = aus der verknüpften Notiz (Raumnummer bzw. Titel). */
  label: string;
  noteId: string | null;
  noteTitle: string | null;
  noteTypeKey: string | null;
  visibility: PinVisibility;
  color: string | null;
}

export interface MapTokenDTO {
  id: string;
  x: number;
  y: number;
  name: string;
  /** Größe in Feldern (1 = mittelgroß, 2 = groß …). */
  size: number;
  color: string;
  noteId: string | null;
  combatantId: string | null;
  hidden: boolean;
  /** Eigenes Bild freier Tokens (`[[Bild.png]]`); gekoppelte Tokens nutzen das Token-Bild ihrer Notiz. */
  image: string | null;
  /** Aufgelöstes Bild (eigenes oder aus der Notiz). */
  imageUrl: string | null;
  /** Kampfstatus, wenn an einen Kampfteilnehmer gekoppelt. */
  combat: TokenCombatInfo | null;
}

/** Fläche nach 5e: Kegel, Kreis (Radius), Quadrat (Würfel). Von (x, y) in Richtung (x2, y2). */
export type TemplateKind = 'cone' | 'circle' | 'square';

export interface MapTemplate {
  id: string;
  kind: TemplateKind;
  x: number;
  y: number;
  x2: number;
  y2: number;
  color: string;
}

export interface MapDTO {
  noteId: string;
  /** Vom DM gelegte Schablonen (auch für Spieler sichtbar). */
  templates: MapTemplate[];
  /** Bildgröße, sobald ein Client das Bild geladen hat. */
  image: { width: number; height: number } | null;
  grid: MapGrid;
  fog: FogState;
  pins: MapPinDTO[];
  tokens: MapTokenDTO[];
  updatedAt: string;
}

const num = z.number().finite();
export const gridSchema = z.object({
  type: z.enum(['none', 'square', 'hex']),
  size: num.min(4).max(2000),
  offsetX: num.min(-5000).max(5000),
  offsetY: num.min(-5000).max(5000),
  cellDistance: num.min(0).max(10000),
  cellUnit: z.string().max(20),
  color: z.string().regex(/^#[0-9a-f]{6}$/i),
  opacity: num.min(0).max(1),
});
const hide = { hide: z.boolean().optional() };
const fogShapeSchema = z.union([
  z.object({ type: z.literal('rect'), x: num, y: num, w: num.min(0), h: num.min(0), ...hide }),
  z.object({ type: z.literal('poly'), points: z.array(num).min(6).max(4000), ...hide }),
  z.object({ type: z.literal('line'), points: z.array(num).min(2).max(4000), width: num.min(1).max(5000), ...hide }),
]);
export const fogSchema = z.object({ enabled: z.boolean(), revealed: z.array(fogShapeSchema).max(2000) });
export const updateMapSchema = z.object({
  grid: gridSchema.partial().optional(),
  fog: fogSchema.optional(),
  image: z.object({ width: z.number().int().min(1).max(100000), height: z.number().int().min(1).max(100000) }).optional(),
  templates: z.array(z.lazy(() => templateSchema)).max(200).optional(),
});

export const templateSchema = z.object({
  id: z.string().min(1).max(40),
  kind: z.enum(['cone', 'circle', 'square']),
  x: num,
  y: num,
  x2: num,
  y2: num,
  color: z.string().regex(/^#[0-9a-f]{6}$/i),
});

export function normalizeTemplates(raw: unknown): MapTemplate[] {
  const parsed = z.array(templateSchema).safeParse(raw);
  return parsed.success ? parsed.data : [];
}

/** Wo neue Kampf-Tokens erscheinen (Mitte des sichtbaren Kartenausschnitts). */
export const spawnSchema = z.object({ spawn: z.object({ x: num, y: num }).optional() });

export const createPinSchema = z.object({
  x: num,
  y: num,
  label: z.string().max(100).default(''),
  noteId: z.string().uuid().nullable().default(null),
  visibility: z.enum(['dm', 'all']).default('dm'),
  color: z.string().regex(/^#[0-9a-f]{6}$/i).nullable().default(null),
});
export const updatePinSchema = createPinSchema.partial();

/** Normalisiert gespeicherte Rasterdaten (fehlende Felder mit Standardwerten). */
export function normalizeGrid(raw: unknown): MapGrid {
  const parsed = gridSchema.partial().safeParse(raw ?? {});
  return { ...DEFAULT_GRID, ...(parsed.success ? parsed.data : {}) };
}

export function normalizeFog(raw: unknown): FogState {
  const parsed = fogSchema.safeParse(raw);
  return parsed.success ? parsed.data : { ...DEFAULT_FOG };
}

/**
 * Kurzbeschriftung eines Pins aus dem Notiztitel: Raumnummern („5. (Höhle) Mine“ → „5“,
 * „12. und 13. Saal“ → „12/13“), sonst der Titel.
 */
export function pinLabelFromTitle(title: string): string {
  const both = /^\s*(\d+)\.\s*und\s*(\d+)\./i.exec(title);
  if (both) return `${both[1]}/${both[2]}`;
  const m = /^\s*(\d+)\./.exec(title);
  return m ? m[1]! : title;
}

// ---------- Tokens ----------

/** Kampfstatus eines Tokens, wenn er an einen Kampfteilnehmer gekoppelt ist. */
export interface TokenCombatInfo {
  kind: 'pc' | 'npc' | 'monster' | 'other';
  hpCurrent: number | null;
  hpMax: number | null;
  tempHp: number;
  defeated: boolean;
  dead: boolean;
  /** Am Zug. */
  active: boolean;
  conditions: string[];
}

export const createTokenSchema = z.object({
  x: num,
  y: num,
  name: z.string().trim().min(1).max(100),
  size: num.min(0.25).max(10).default(1),
  color: z.string().regex(/^#[0-9a-f]{6}$/i).default('#c0392b'),
  noteId: z.string().uuid().nullable().default(null),
  hidden: z.boolean().default(false),
  image: z.string().max(500).nullable().default(null),
});
export const updateTokenSchema = createTokenSchema.partial();

/** Standardfarben nach Art des Kampfteilnehmers. */
export const TOKEN_COLORS: Record<TokenCombatInfo['kind'], string> = { pc: '#3b82c4', npc: '#3f9b5d', monster: '#b83a2e', other: '#8a7a66' };

/** Größenkategorie des Statblocks → Felder (winzig ½, klein/mittel 1, groß 2, riesig 3, gigantisch 4). */
export function tokenCellsForSize(size: string | undefined): number {
  switch ((size ?? '').toLowerCase()) {
    case 'tiny':
      return 0.5;
    case 'large':
      return 2;
    case 'huge':
      return 3;
    case 'gargantuan':
      return 4;
    default:
      return 1;
  }
}

/** Mittelpunkt des Hexfelds (Spitze oben) an einer Position. */
function hexCenter(grid: MapGrid, x: number, y: number): { x: number; y: number } {
  const w = grid.size;
  const r = w / Math.sqrt(3);
  const rowH = 1.5 * r;
  const approxRow = Math.round((y - grid.offsetY) / rowH);
  let best = { x, y, d: Infinity };
  for (let row = approxRow - 1; row <= approxRow + 1; row++) {
    const cy = grid.offsetY + row * rowH;
    const shift = row % 2 !== 0 ? w / 2 : 0;
    const col = Math.round((x - grid.offsetX - shift) / w);
    for (let c = col - 1; c <= col + 1; c++) {
      const cx = grid.offsetX + c * w + shift;
      const d = (cx - x) ** 2 + (cy - y) ** 2;
      if (d < best.d) best = { x: cx, y: cy, d };
    }
  }
  return { x: best.x, y: best.y };
}

/**
 * Rastet einen Token-Mittelpunkt ein. Quadrate: Feldmitte bei ungerader Größe,
 * Feldecke bei gerader Größe (großer Token belegt 2×2). Hex: nächster Feldmittelpunkt.
 */
export function snapToGrid(grid: MapGrid, x: number, y: number, cells = 1): { x: number; y: number } {
  if (grid.type === 'none') return { x, y };
  if (grid.type === 'hex') return hexCenter(grid, x, y);
  const s = grid.size;
  const even = Math.round(cells) % 2 === 0 && cells >= 1;
  const base = (v: number, off: number) => {
    const rel = (v - off) / s;
    return off + (even ? Math.round(rel) : Math.floor(rel) + 0.5) * s;
  };
  return { x: base(x, grid.offsetX), y: base(y, grid.offsetY) };
}

/** Strecke zwischen zwei Punkten in Feldern (Quadrate: 5e-Standard, Diagonale = 1 Feld). */
export function gridDistance(grid: MapGrid, a: { x: number; y: number }, b: { x: number; y: number }): number {
  const dx = Math.abs(b.x - a.x) / grid.size;
  const dy = Math.abs(b.y - a.y) / grid.size;
  if (grid.type === 'hex') return Math.round(Math.hypot(dx, dy));
  return Math.round(Math.max(dx, dy));
}

// ---------- Nebel ----------

function inPolygon(points: number[], x: number, y: number): boolean {
  let inside = false;
  for (let i = 0, j = points.length - 2; i < points.length; j = i, i += 2) {
    const xi = points[i]!, yi = points[i + 1]!, xj = points[j]!, yj = points[j + 1]!;
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

function nearPolyline(points: number[], width: number, x: number, y: number): boolean {
  const r2 = (width / 2) ** 2;
  if (points.length === 2) return (points[0]! - x) ** 2 + (points[1]! - y) ** 2 <= r2;
  for (let i = 0; i + 3 < points.length; i += 2) {
    const ax = points[i]!, ay = points[i + 1]!, bx = points[i + 2]!, by = points[i + 3]!;
    const dx = bx - ax, dy = by - ay;
    const len2 = dx * dx + dy * dy;
    const t = len2 === 0 ? 0 : Math.max(0, Math.min(1, ((x - ax) * dx + (y - ay) * dy) / len2));
    if ((ax + t * dx - x) ** 2 + (ay + t * dy - y) ** 2 <= r2) return true;
  }
  return false;
}

export function fogShapeContains(shape: FogShape, x: number, y: number): boolean {
  if (shape.type === 'rect') return x >= shape.x && x <= shape.x + shape.w && y >= shape.y && y <= shape.y + shape.h;
  if (shape.type === 'poly') return inPolygon(shape.points, x, y);
  return nearPolyline(shape.points, shape.width, x, y);
}

/** Ist der Punkt für Spieler sichtbar? Ohne aktiven Nebel immer; sonst gilt der letzte Schritt, der ihn trifft. */
export function isRevealed(fog: FogState, x: number, y: number): boolean {
  if (!fog.enabled) return true;
  let visible = false;
  for (const s of fog.revealed) if (fogShapeContains(s, x, y)) visible = !s.hide;
  return visible;
}

// ---------- Schablonen und Messen ----------

/** Feldgröße in Pixeln (ohne Raster: Standardgröße als Maßstab). */
function cellPx(grid: MapGrid): number {
  return grid.size > 0 ? grid.size : DEFAULT_GRID.size;
}

/** Rastet einen Messpunkt ein: Quadrate auf Feldecken und -mitten, Hex auf Feldmitten. */
export function snapMeasurePoint(grid: MapGrid, x: number, y: number): { x: number; y: number } {
  if (grid.type === 'none') return { x, y };
  if (grid.type === 'hex') return snapToGrid(grid, x, y);
  const h = grid.size / 2;
  return { x: grid.offsetX + Math.round((x - grid.offsetX) / h) * h, y: grid.offsetY + Math.round((y - grid.offsetY) / h) * h };
}

/** Lineal: Strecke nach 5e (Diagonale = 1 Feld) in der Einheit der Karte. */
export function rulerDistance(grid: MapGrid, a: { x: number; y: number }, b: { x: number; y: number }): number {
  const cells = grid.type === 'none' ? Math.hypot(b.x - a.x, b.y - a.y) / cellPx(grid) : gridDistance(grid, a, b);
  return Math.round(cells * grid.cellDistance * 10) / 10;
}

export interface TemplateShape {
  /** Größe in ganzen Feldern (Länge, Radius bzw. Kantenlänge). */
  cells: number;
  /** Größe in der Einheit der Karte. */
  distance: number;
  /** Kegel/Quadrat: Eckpunkte; Kreis: leer. */
  points: number[];
  radius: number;
}

/**
 * Geometrie einer Schablone. Größen werden auf ganze Felder gerundet (5e: 1,5-m-Schritte).
 * Kegel: Breite am Ende = Länge; Kreis: Radius; Quadrat: Ecke am Ursprung.
 */
export function templateShape(grid: MapGrid, t: Pick<MapTemplate, 'kind' | 'x' | 'y' | 'x2' | 'y2'>): TemplateShape {
  const size = cellPx(grid);
  const dx = t.x2 - t.x;
  const dy = t.y2 - t.y;
  const len = t.kind === 'square' ? Math.max(Math.abs(dx), Math.abs(dy)) : Math.hypot(dx, dy);
  const cells = Math.max(1, Math.round(len / size));
  const L = cells * size;
  const distance = Math.round(cells * grid.cellDistance * 10) / 10;
  if (t.kind === 'circle') return { cells, distance, points: [], radius: L };
  if (t.kind === 'square') {
    const sx = dx < 0 ? -1 : 1;
    const sy = dy < 0 ? -1 : 1;
    return { cells, distance, radius: 0, points: [t.x, t.y, t.x + sx * L, t.y, t.x + sx * L, t.y + sy * L, t.x, t.y + sy * L] };
  }
  const d = Math.hypot(dx, dy) || 1;
  const ux = dx / d;
  const uy = dy / d;
  const ex = t.x + ux * L;
  const ey = t.y + uy * L;
  return { cells, distance, radius: 0, points: [t.x, t.y, ex - uy * (L / 2), ey + ux * (L / 2), ex + uy * (L / 2), ey - ux * (L / 2)] };
}
